import type { FC, PropsWithChildren } from "react";
import React, { useContext, useEffect, useMemo } from "react";

import type { Config } from "@jti/fe-common/config";
import { getConfig, getLocale } from "@jti/fe-common/config";
import type { Direction } from "@jti/fe-common/providers";
import { DirectionContext } from "@jti/fe-common/providers";
import { TranslationsContext } from "@jti/fe-common/translation/TranslationsContext";

import { ConfigContext, LocaleContext } from "./contexts";

type ConfigProviderProps = Partial<{
  config: Config;
  translations: Record<string, string>;
  locale: string;
  langWithCountryCode: string;
  direction: Direction;
}>;

const getInitialLocale = (locale?: string, langWithCountryCode?: string): string | null => {
  const extendedLanguageWithCodeRegex = /^[a-z]{2}-[A-Z]{2}$/;

  if (locale && extendedLanguageWithCodeRegex.test(locale)) {
    return locale;
  }

  if (langWithCountryCode && extendedLanguageWithCodeRegex.test(langWithCountryCode)) {
    return langWithCountryCode;
  }

  return locale ?? null;
};

export const ConfigProvider: FC<PropsWithChildren<ConfigProviderProps>> = ({
  children,
  config,
  locale,
  langWithCountryCode,
  translations,
  direction,
}) => {
  const [configValue, setConfigValue] = React.useState<Config | null>(config ?? null);
  const [localeValue, setLocaleValue] = React.useState<string | null>(() =>
    getInitialLocale(locale, langWithCountryCode)
  );
  const parentTranslations = useContext(TranslationsContext);
  const resolvedTranslations = translations ?? parentTranslations;

  useEffect(() => {
    // should we set those values only when we are not in next.js app?
    if (!config) {
      setConfigValue(getConfig());
    }
    if (!locale) {
      setLocaleValue(getLocale());
    }
  }, [config, locale]);

  const memoizedConfigValue = useMemo(() => configValue, [configValue]);
  const memoizedLocaleValue = useMemo(() => localeValue, [localeValue]);
  const memoizedTranslationsValue = useMemo(() => resolvedTranslations ?? null, [resolvedTranslations]);
  const memoizedDirection = useMemo(() => direction ?? null, [direction]);

  if (!configValue || !localeValue || !resolvedTranslations) {
    return null;
  }

  return (
    <ConfigContext.Provider value={memoizedConfigValue}>
      <LocaleContext.Provider value={memoizedLocaleValue}>
        <TranslationsContext.Provider value={memoizedTranslationsValue}>
          <DirectionContext.Provider value={memoizedDirection}>{children}</DirectionContext.Provider>
        </TranslationsContext.Provider>
      </LocaleContext.Provider>
    </ConfigContext.Provider>
  );
};

export function withConfigProvider<Props extends object>(Component: FC<Props>, config: ConfigProviderProps = {}) {
  return (props: Props) => {
    return (
      <ConfigProvider {...config}>
        <Component {...props} />
      </ConfigProvider>
    );
  };
}
